/**
 * Binds the WHO HAS ACCESS group line to the share hook's keyed handlers
 * (SE-8359).
 * @module features/scouting/components/molecules/ShareGroupAccessRow/ShareGroupAccessRowContainer
 */

import { useCallback } from "react";

import ShareGroupAccessRowView from "./ShareGroupAccessRowView";

import type { ShareGroupAccessRowItem } from "@/features/scouting/utils/shareGroupGrants";
import {
  shareRoleLabel,
  shareRoleOptionLabel,
} from "@/features/scouting/utils/shareWatchlistGrants";

/** Props for `ShareGroupAccessRowContainer`. */
export interface ShareGroupAccessRowContainerProps {
  /** The group grant to render. */
  readonly row: ShareGroupAccessRowItem;
  /** Role change handler keyed by group grant id. */
  readonly onChangeRole: (groupGrantId: string, value: string) => void;
  /** Revoke handler keyed by group grant id. */
  readonly onRevoke: (groupGrantId: string) => void;
}

/**
 * "183 people · Viewer" — the audience the grant reaches and its role.
 * @param memberCount - ACTIVE members reached
 * @param roleLabel - "Editor" / "Viewer"
 * @returns The detail line
 */
const detailLine = (memberCount: number, roleLabel: string): string =>
  `${memberCount} ${memberCount === 1 ? "person" : "people"} · ${roleLabel}`;

/**
 * Renders the group line with its handlers bound to the grant id.
 * @param root0 - Component props
 * @param root0.row - The group grant to render
 * @param root0.onChangeRole - Role change handler keyed by group grant id
 * @param root0.onRevoke - Revoke handler keyed by group grant id
 * @returns The bound line view.
 */
const ShareGroupAccessRowContainer = ({
  row,
  onChangeRole,
  onRevoke,
}: ShareGroupAccessRowContainerProps) => {
  const handleRoleChange = useCallback(
    (value: string) => {
      onChangeRole(row.groupGrantId, value);
    },
    [onChangeRole, row.groupGrantId]
  );
  const handleRevoke = useCallback(() => {
    onRevoke(row.groupGrantId);
  }, [onRevoke, row.groupGrantId]);

  return (
    <ShareGroupAccessRowView
      groupGrantId={row.groupGrantId}
      label={row.label}
      detail={detailLine(row.memberCount, shareRoleLabel(row.role))}
      role={row.role}
      roleOptionLabel={shareRoleOptionLabel(row.role)}
      isPending={row.isPending}
      onRoleChange={handleRoleChange}
      onRevoke={handleRevoke}
    />
  );
};

ShareGroupAccessRowContainer.displayName = "ShareGroupAccessRowContainer";

export default ShareGroupAccessRowContainer;
